import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dialog, EmptyState, Icon, Input, Segmented, Select, useToast } from "@agentsaas/ui";
import { pickLocalized, type Capability } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { formatDateTime } from "../app/format";
import { Loading } from "../layout/Loading";
import { NotFound } from "../pages/NotFound";
import { capk, useCapabilities, useCapability, useCapabilityVersions, useMcpServers } from "../data/capabilities";
import { CapBadges, CapFrame, CapIcon, FileViewer, SkillFileTree, useCategoryLabel } from "./common";
import { capKey, CATEGORIES, toolCount } from "./model";

/**
 * /t/:tid/capabilities：能力库（内置 + 本租户）。分类筛选（?cat=）+ 搜索（?q=，防抖写 URL）；
 * 卡片：图标、名称、描述、skill 数、工具数、版本与来源。
 */
export function CapabilityLibraryPage() {
  const { tid = "" } = useParams();
  const { t, locale } = useT();
  const catLabel = useCategoryLabel();
  const [sp, setSp] = useSearchParams();
  const q = sp.get("q") ?? "";
  const cat = sp.get("cat") ?? "";
  const [text, setText] = useState(q);
  const set = (k: string, v: string) =>
    setSp(
      (cur) => {
        const n = new URLSearchParams(cur);
        if (v) n.set(k, v);
        else n.delete(k);
        return n;
      },
      { replace: true },
    );
  useEffect(() => {
    if (text.trim() === q) return;
    const h = setTimeout(() => set("q", text.trim()), 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  const list = useCapabilities(tid, { category: cat, q });
  const all = useCapabilities(tid);
  const cats = useMemo(() => {
    const seen = new Set((all.data ?? []).map((c) => c.manifest.category));
    return [...CATEGORIES.filter((c) => seen.has(c)), ...[...seen].filter((c) => !(CATEGORIES as readonly string[]).includes(c)).sort()];
  }, [all.data]);
  const items = list.data ?? [];
  return (
    <CapFrame section="library">
      <div className="stack">
        <div className="page__head">
          <h1 className="page__title">{t("cap.nav.library")}</h1>
          <Link className="ui-btn ui-btn--primary ui-btn--md" to={`/t/${tid}/capabilities/new`} data-testid="cap-new">
            <Icon name="plus" />
            {t("cap.new")}
          </Link>
        </div>
        <div className="filters row" role="search">
          <Input type="search" aria-label={t("cap.search")} placeholder={t("cap.search")} value={text} onChange={(e) => setText(e.target.value)} data-testid="cap-search" />
          <Select aria-label={t("cap.category")} value={cat} onChange={(e) => set("cat", e.target.value)} data-testid="cap-category">
            <option value="">{t("cap.category.all")}</option>
            {cats.map((c) => (
              <option key={c} value={c}>
                {catLabel(c)}
              </option>
            ))}
          </Select>
        </div>
        {list.isPending ? (
          <Loading />
        ) : items.length ? (
          <ul className="cap-grid" data-testid="cap-list">
            {items.map((c) => (
              <li key={`${c.tenantId ?? "builtin"}:${c.id}`}>
                <Link to={`/t/${tid}/capabilities/${c.id}`} className="card cap-card" data-testid="cap-card" data-cap={c.id}>
                  <div className="cap-card__head">
                    <CapIcon icon={c.manifest.icon} />
                    <strong className="grow ellipsis">{pickLocalized(c.manifest.name, locale)}</strong>
                  </div>
                  <p className="small muted cap-card__desc">{pickLocalized(c.manifest.description, locale)}</p>
                  <div className="row cap-card__foot small">
                    <Badge tone="neutral">{catLabel(c.manifest.category)}</Badge>
                    <CapBadges cap={c} />
                  </div>
                  <div className="row small muted">
                    <span data-testid="cap-card-skills">{t("cap.skills_count", { count: c.manifest.skills.length })}</span>
                    <span className="sep" aria-hidden="true" />
                    <span data-testid="cap-card-tools">{t("cap.tools_count", { count: toolCount(c.manifest) })}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState icon="sparkles" title={t("cap.empty")} />
        )}
      </div>
    </CapFrame>
  );
}

type DetailTab = "overview" | "files" | "manifest" | "versions";
const TABS: DetailTab[] = ["overview", "files", "manifest", "versions"];

/** /t/:tid/capabilities/:capid[?v=&tab=&skill=&file=]：详情（manifest、skill 文件树与只读代码、版本历史） */
export function CapabilityDetailPage() {
  const { tid = "", capid = "" } = useParams();
  const { t, locale } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const { api } = useServices();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const v = Number(sp.get("v")) || null;
  const tab = (TABS.includes(sp.get("tab") as DetailTab) ? sp.get("tab") : "overview") as DetailTab;
  const cap = useCapability(tid, capid, v);
  const versions = useCapabilityVersions(tid, capid);
  const [deprecating, setDeprecating] = useState(false);
  const set = (patch: Record<string, string | null>) =>
    setSp(
      (cur) => {
        const n = new URLSearchParams(cur);
        for (const [k, x] of Object.entries(patch)) {
          if (x) n.set(k, x);
          else n.delete(k);
        }
        return n;
      },
      { replace: true },
    );
  if (cap.isError) return <NotFound />;
  if (cap.isPending)
    return (
      <CapFrame section="library">
        <Loading />
      </CapFrame>
    );
  const c = cap.data;
  const latest = versions.data?.[0]?.version ?? c.version;
  const own = !!c.tenantId;
  const deprecate = async () => {
    try {
      await api.call("capabilityDeprecate", { params: { tid, capid } });
      toast.show({ message: t("cap.deprecated_done"), tone: "success" });
      await qc.invalidateQueries({ queryKey: capk.all });
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
    } finally {
      setDeprecating(false);
    }
  };
  return (
    <CapFrame section="library">
      <div className="stack" data-testid="cap-detail">
        <div className="page__head cap-detail__head">
          <div className="row grow">
            <CapIcon icon={c.manifest.icon} size={24} />
            <h1 className="page__title" data-testid="cap-title">
              {pickLocalized(c.manifest.name, locale)}
            </h1>
            <code className="muted">{c.id}</code>
            <CapBadges cap={c} />
          </div>
          <div className="row">
            <Link className="ui-btn ui-btn--ghost ui-btn--md" to={`/t/${tid}/capabilities`}>
              {t("common.back")}
            </Link>
            {own && c.status === "active" ? (
              <>
                <Link className="ui-btn ui-btn--secondary ui-btn--md" to={`/t/${tid}/capabilities/${capid}/edit`} data-testid="cap-edit">
                  <Icon name="edit" />
                  {t("cap.edit")}
                </Link>
                <Button variant="ghost" icon="trash" onClick={() => setDeprecating(true)} data-testid="cap-deprecate">
                  {t("cap.deprecate")}
                </Button>
              </>
            ) : null}
            {!own ? (
              <Link className="ui-btn ui-btn--ghost ui-btn--md" to={`/t/${tid}/capabilities/new?from=${capid}`} data-testid="cap-duplicate">
                <Icon name="copy" />
                {t("cap.duplicate")}
              </Link>
            ) : null}
          </div>
        </div>
        {c.version !== latest ? (
          <p className="banner banner--warning small" role="status">
            {t("cap.old_version", { version: c.version, latest })}{" "}
            <button type="button" className="linklike" onClick={() => set({ v: null })}>
              {t("cap.view_latest")}
            </button>
          </p>
        ) : null}
        <Segmented<DetailTab> label={t("cap.sections")} value={tab} onChange={(x) => set({ tab: x === "overview" ? null : x })} options={TABS.map((x) => ({ value: x, label: t(`cap.tab.${x}`) }))} />
        {tab === "overview" ? <Overview cap={c} tid={tid} /> : null}
        {tab === "files" ? <SkillFiles cap={c} skill={sp.get("skill")} file={sp.get("file")} onPick={(skill, file) => set({ skill, file })} /> : null}
        {tab === "manifest" ? <ManifestJson cap={c} /> : null}
        {tab === "versions" ? (
          versions.isPending ? (
            <Loading />
          ) : (
            <ol className="card cap-versions" data-testid="cap-versions">
              {(versions.data ?? []).map((x) => (
                <li key={x.version} className="row">
                  <button type="button" className={`linklike${x.version === c.version ? " is-active" : ""}`} aria-current={x.version === c.version ? "true" : undefined} onClick={() => set({ v: x.version === latest ? null : String(x.version), tab: null })} data-testid="cap-version">
                    {t("cap.version", { version: x.version })}
                  </button>
                  {x.version === latest ? <Badge tone="success">{t("cap.latest")}</Badge> : null}
                  {x.status === "deprecated" ? <Badge tone="warning">{t("cap.deprecated")}</Badge> : null}
                  <span className="grow" />
                  <span className="muted small">{formatDateTime(x.createdAt, locale)}</span>
                </li>
              ))}
            </ol>
          )
        ) : null}
      </div>
      <Dialog
        open={deprecating}
        onClose={() => setDeprecating(false)}
        title={t("cap.deprecate")}
        closeLabel={t("shell.close")}
        size="sm"
        footer={
          <>
            <Button onClick={() => setDeprecating(false)}>{t("common.cancel")}</Button>
            <Button variant="danger" onClick={() => void deprecate()} data-testid="cap-deprecate-confirm">
              {t("cap.deprecate")}
            </Button>
          </>
        }
      >
        <p>{t("cap.deprecate_confirm")}</p>
      </Dialog>
    </CapFrame>
  );
}

function Overview({ cap, tid }: { cap: Capability; tid: string }) {
  const { t, locale } = useT();
  const catLabel = useCategoryLabel();
  const servers = useMcpServers(cap.tenantId ? tid : "");
  const m = cap.manifest;
  const none = <span className="muted">{t("cap.none")}</span>;
  return (
    <div className="stack">
      <section className="card stack">
        <p>{pickLocalized(m.description, locale)}</p>
        <dl className="kv">
          <dt>{t("cap.category")}</dt>
          <dd>{catLabel(m.category)}</dd>
          <dt>{t("cap.skills")}</dt>
          <dd>{m.skills.length ? m.skills.map((s) => <code key={s.name} className="cap-pill">{s.name}</code>) : none}</dd>
          <dt>{t("cap.tools.scopes")}</dt>
          <dd>{m.tools.mcpScopes.length ? m.tools.mcpScopes.map((s) => <code key={s} className="cap-pill">{s}</code>) : none}</dd>
          <dt>{t("cap.tools.allowed")}</dt>
          <dd>{m.tools.allowedTools.length ? m.tools.allowedTools.map((s) => <code key={s} className="cap-pill">{s}</code>) : none}</dd>
          <dt>{t("cap.tools.external")}</dt>
          <dd>
            {m.tools.externalMcp.length
              ? m.tools.externalMcp.map((x) => (
                  <code key={x.name} className="cap-pill">
                    {x.name}
                    {servers.data?.find((s) => s.id === x.serverId) ? ` (${servers.data.find((s) => s.id === x.serverId)!.name})` : ""}
                  </code>
                ))
              : none}
          </dd>
          <dt>{t("cap.tools.rules")}</dt>
          <dd>
            {m.tools.permissionRules.length
              ? m.tools.permissionRules.map((r, i) => (
                  <code key={i} className="cap-pill">
                    {r.tool}
                    {r.pattern ? `(${r.pattern})` : ""}: {r.mode}
                  </code>
                ))
              : none}
          </dd>
          <dt>{t("cap.runtime.image")}</dt>
          <dd>{m.runtime.image ? <code>{m.runtime.image}</code> : <span className="muted">{t("cap.runtime.image_default")}</span>}</dd>
          <dt>{t("cap.runtime.egress")}</dt>
          <dd>{m.runtime.egress.length ? m.runtime.egress.map((s) => <code key={s} className="cap-pill">{s}</code>) : none}</dd>
        </dl>
      </section>
      {m.params.length ? (
        <section className="card stack">
          <h2 className="section-title">{t("cap.params")}</h2>
          <table className="table table--stack">
            <thead>
              <tr>
                <th>{t("cap.param.key")}</th>
                <th>{t("cap.param.label")}</th>
                <th>{t("cap.param.type")}</th>
                <th>{t("cap.param.default")}</th>
              </tr>
            </thead>
            <tbody>
              {m.params.map((p) => (
                <tr key={p.key}>
                  <td>
                    <code>{p.key}</code>
                  </td>
                  <td>{pickLocalized(p.label, locale)}</td>
                  <td>
                    {t(`cap.param.type.${p.type}`)}
                    {p.options?.length ? <span className="muted small"> ({p.options.join(" / ")})</span> : null}
                  </td>
                  <td>{p.default === undefined ? none : <code>{String(p.default)}</code>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
      {m.instructions ? (
        <section className="card stack">
          <h2 className="section-title">{t("cap.instructions")}</h2>
          <pre className="cap-pre">{m.instructions}</pre>
        </section>
      ) : null}
    </div>
  );
}

function SkillFiles({ cap, skill, file, onPick }: { cap: Capability; skill: string | null; file: string | null; onPick(skill: string, file: string): void }) {
  const { t } = useT();
  const skills = cap.manifest.skills;
  if (!skills.length) return <EmptyState icon="file" title={t("cap.files.none")} />;
  const s = skills.find((x) => x.name === skill) ?? skills[0]!;
  const path = file && s.files.some((f) => f.path === file) ? file : "SKILL.md";
  const f = s.files.find((x) => x.path === path);
  return (
    <div className="cap-files">
      <div className="cap-files__side card">
        {skills.length > 1 ? (
          <Select aria-label={t("cap.skill")} value={s.name} onChange={(e) => onPick(e.target.value, "SKILL.md")} data-testid="cap-skill-select">
            {skills.map((x) => (
              <option key={x.name} value={x.name}>
                {x.name}
              </option>
            ))}
          </Select>
        ) : (
          <code className="cap-files__skill">{s.name}</code>
        )}
        <SkillFileTree files={s.files} selected={path} onSelect={(p) => onPick(s.name, p)} label={t("cap.files.tree", { skill: s.name })} />
      </div>
      <div className="cap-files__main card">
        <FileViewer file={f} pathKey={`${capKey(cap)}/${s.name}`} />
      </div>
    </div>
  );
}

function ManifestJson({ cap }: { cap: Capability }) {
  // skill 文件内容太长，manifest 视图里只列文件路径
  const view = {
    ...cap.manifest,
    skills: cap.manifest.skills.map((s) => ({ name: s.name, files: s.files.map((f) => `${f.path}${f.executable ? " (x)" : ""}`) })),
  };
  return (
    <div className="card cap-viewer__body cap-manifest" data-testid="cap-manifest">
      <FileViewer file={{ path: "capability.json", content: JSON.stringify(view, null, 2), encoding: "utf8", executable: false }} pathKey={capKey(cap)} />
    </div>
  );
}
